import React, { useEffect, useState } from 'react';
import './findText.scss'
import { connect } from 'react-redux';
import { ADD_FOUND_WORDS, SET_CASE_MATCH, SET_FOUND_WORDS, SET_SEARCH_INDEX, SET_SEARCH_TEXT } from '../../sagas/constants';
import {
    Input,
  } from "@material-ui/core";
import AmberCheckbox from '../inputs/AmberCheckbox';
import AmberPrimaryButton from '../buttons/AmberPrimaryButton';
import PropTypes from 'prop-types';

const mapStateToProps = ({ findText }) => ({ findText });

function extractTextFromSlateNodes(nodes) {
  return nodes.flatMap((node) => {
    if (typeof node.text === 'string') {
      return [node.text];
    }

    if (Array.isArray(node.children)) {
      return [extractTextFromSlateNodes(node.children)];
    }

    return [];
  }).filter(Boolean).join(' ');
}

function pasteSlateFragmentAsText(e, currentValue, setValue) {
  if (e.clipboardData.getData('text/plain')) return;
  const fragment = e.clipboardData.getData('application/x-slate-fragment');
  if (!fragment) return;
  e.preventDefault();
  try {
    const nodes = JSON.parse(decodeURIComponent(window.atob(fragment)));
    const text = extractTextFromSlateNodes(nodes).trim().replace(/\s+/g, ' ');
    if (!text) return;
    const { selectionStart: start, selectionEnd: end } = e.target;
    setValue(currentValue.slice(0, start) + text + currentValue.slice(end));
  } catch (_) {}
}

function FindText({ 
    transcriptJson,
    editor,
    onSeekAndCenterAudio,
    dispatch,
    findText: {
        isCheckedCaseMatch,
        searchText,
        foundStartWords,
        searchIndex,
    }}) {
  
  const [replaceWithString, setReplaceWithString] = useState('');

  useEffect(() => {
    if (searchIndex !== undefined) {
        seekToSeachWord();
    }
  }, [searchIndex])

  useEffect(() => {
    return () => {
        dispatch({type: SET_FOUND_WORDS, value: []});
        dispatch({type: SET_SEARCH_INDEX, value: undefined});
    }
  }, [])

  const handleCheckboxChange = () => {
    dispatch({type: SET_CASE_MATCH, value: !isCheckedCaseMatch});
    dispatch({type: SET_FOUND_WORDS, value: []});
    dispatch({type: SET_SEARCH_INDEX, value: undefined});
  };

  const handleSearchInputChange = (event) => {
    dispatch({type: SET_SEARCH_TEXT, value: event.target.value});
    dispatch({type: SET_FOUND_WORDS, value: []});
    dispatch({type: SET_SEARCH_INDEX, value: undefined});
  };

  function isSearchWordInSegment(segment, index, searchWord, searchIdx, isCheckedCaseMatch) {
    let segmentWord = segment.words[index + searchIdx]?.text;
    if (isCheckedCaseMatch) {
      return segmentWord && segmentWord.indexOf(searchWord) !== -1;
    }
    return (
      segmentWord &&
      segmentWord.toLowerCase().indexOf(searchWord.toLowerCase()) !== -1
    );
  }

  function findMatchingWordsInSegment(segment, searchText, isCheckedCaseMatch) {
    const foundWords = [];
    const segmentText = segment.words.map((word) => word?.text).join(' ');
    const isSearchInSegment = isCheckedCaseMatch
      ? segmentText.includes(searchText)
      : segmentText.toLowerCase().includes(searchText.toLowerCase());

    if (!isSearchInSegment) return foundWords;

    segment.words.forEach((word, index) => {
      const foundWord = searchText
        .split(" ")
        .every((searchWord, searchIdx) =>
          isSearchWordInSegment(segment, index, searchWord, searchIdx, isCheckedCaseMatch)
        );
      if (foundWord) {
        foundWords.push(word);
      }
    });

    return foundWords;
  }

  const handleSearch = () => {
    dispatch({ type: SET_FOUND_WORDS, value: [] });
    dispatch({ type: SET_SEARCH_INDEX, value: undefined });

    let allFoundWords = [];
    transcriptJson.segments.forEach((segment) => {
      allFoundWords = allFoundWords.concat(
        findMatchingWordsInSegment(segment, searchText, isCheckedCaseMatch)
      );
    });

    allFoundWords.forEach((word) => {
      dispatch({ type: ADD_FOUND_WORDS, value: word });
    });

    dispatch({ type: SET_SEARCH_INDEX, value: 0 });
  };
  
  const handleSearchPrevious = () => {
    const previousIndex = searchIndex > 0 ? searchIndex - 1 : foundStartWords.length - 1
    dispatch({type: SET_SEARCH_INDEX, value: previousIndex});
  }

  const handleSearchNext = () => {
    dispatch({type: SET_SEARCH_INDEX, value: (searchIndex + 1) % foundStartWords.length});
  }

  const seekToSeachWord = () => {
    if (foundStartWords.length > 0) {
        editor.setAudioPlayerTime(foundStartWords[searchIndex].start, true, false);
        onSeekAndCenterAudio(foundStartWords[searchIndex].start);
        // Need to wait for seek to render new content before scrolling down to be able to see
        // what is searched for in text field.
        setTimeout(() => {
            window.scrollTo(window.scrollX, window.scrollY + 30);
        }, 101)
    }
  }

  const handleReplaceAll = () => {
    if (editor) {
      editor.findReplace(searchText, replaceWithString, { caseSensitive: isCheckedCaseMatch })
    }
  }

  const handleReplaceInputChange = (event) => {
    setReplaceWithString(event.target.value);
  }

  return (
    <div className='findWrapper'>
      <Input
        type="text"
        value={searchText}
        onChange={handleSearchInputChange}
        onPaste={(e) => pasteSlateFragmentAsText(e, searchText, v => dispatch({ type: SET_SEARCH_TEXT, value: v }))}
        inputProps={{ maxLength: 64 }}
        autoFocus
      />
      <div className='buttonWrapper' >
        <AmberCheckbox label='Match case' checked={isCheckedCaseMatch} handleChange={handleCheckboxChange} />
        <AmberPrimaryButton disabled={searchText === ""} onClick={handleSearch} text='Find' />
        <AmberPrimaryButton disabled={foundStartWords.length < 2} onClick={handleSearchPrevious} text='Previous' />
        <AmberPrimaryButton disabled={foundStartWords.length < 2} onClick={handleSearchNext} text='Next' />
      </div>
      <div>Replace with</div>
      <Input
        type="text"
        value={replaceWithString}
        onChange={handleReplaceInputChange}
        onPaste={(e) => pasteSlateFragmentAsText(e, replaceWithString, setReplaceWithString)}
        inputProps={{ maxLength: 64 }}
      />
      <div className='buttonWrapper'>
        <AmberPrimaryButton disabled={searchText === "" || replaceWithString === ""} onClick={handleReplaceAll} text='Replace all' />
      </div>
    </div>
  );
}

FindText.propTypes = {
  transcriptJson: PropTypes.shape({
    segments: PropTypes.arrayOf(
      PropTypes.shape({
        words: PropTypes.arrayOf(
          PropTypes.shape({
            text: PropTypes.string,
            start: PropTypes.number,
            end: PropTypes.number,
          })
        ),
      })
    ),
  }),
  editor: PropTypes.shape({
    setAudioPlayerTime: PropTypes.func,
    findReplace: PropTypes.func,
  }),
  onSeekAndCenterAudio: PropTypes.func,
  dispatch: PropTypes.func,
  findText: PropTypes.shape({
    isCheckedCaseMatch: PropTypes.bool,
    searchText: PropTypes.string,
    foundStartWords: PropTypes.arrayOf(
      PropTypes.shape({
        start: PropTypes.number,
      })
    ),
    searchIndex: PropTypes.number,
  }),
};

export default connect(mapStateToProps)(FindText);